import { computed, type Ref } from 'vue';
import { useQueries } from '@tanstack/vue-query';
import { getPhases, getTasks } from '../api/projects';
import type { DraftResponse } from '../api/draft';
import type { VpWbsRequirements } from '../utils/reconcileDraft';

// Asks VP whether a draft row is missing a level it requires: a phase for a
// phase-less row, a task for a row whose phase has tasks. One read per project
// (or project + phase), sharing the ['phases', wbs1] and ['tasks', wbs1, wbs2]
// caches with the pickers (usePhaseCascade); VP's structure does not change
// mid-triage (#6241). A failed read is not "none" (#6238), but it is not
// "required" either, so it flags nothing; VP's own rejection, marked on the
// row, remains the backstop.
export function useVpWbsRequirements(draft: Ref<DraftResponse | null>) {
  const rows = computed(() => (draft.value?.rows ?? []).filter((r) => !r.target.category));

  const phaseCandidates = computed((): string[] =>
    [...new Set(rows.value
      .filter((r) => !r.isPhased && !r.target.wbs2)
      .map((r) => r.target.wbs1))].sort());

  const taskCandidates = computed((): [string, string][] =>
    [...new Set(rows.value
      .filter((r) => !!r.target.wbs2 && !r.target.wbs3 && !r.needsTask)
      .map((r) => `${r.target.wbs1}|${r.target.wbs2}`))].sort()
      .map((k) => k.split('|') as [string, string]));

  const phaseQueries = useQueries({
    queries: computed(() => phaseCandidates.value.map((wbs1) => ({
      queryKey: ['phases', wbs1],
      queryFn: () => getPhases(wbs1),
      staleTime: Infinity,
      retry: false,
    }))),
  });

  const taskQueries = useQueries({
    queries: computed(() => taskCandidates.value.map(([wbs1, wbs2]) => ({
      queryKey: ['tasks', wbs1, wbs2],
      queryFn: () => getTasks(wbs1, wbs2),
      staleTime: Infinity,
      retry: false,
    }))),
  });

  return computed((): VpWbsRequirements => {
    const phased = new Set<string>();
    phaseCandidates.value.forEach((wbs1, i) => {
      if ((phaseQueries.value[i]?.data?.phases.length ?? 0) > 0) phased.add(wbs1);
    });
    const tasked = new Set<string>();
    taskCandidates.value.forEach(([wbs1, wbs2], i) => {
      if ((taskQueries.value[i]?.data?.tasks.length ?? 0) > 0) tasked.add(`${wbs1}|${wbs2}`);
    });
    return { phased, tasked };
  });
}
